import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, ProgressBar, Alert, ModalBody } from 'react-bootstrap';
import { toAbsoluteUrl } from "../../_metronic/_helpers";
import { useSelector } from 'react-redux';
import { post } from '../api/base';
import { getDatabaseNamePath } from '../config/config';

const QuestionnaireModal = ({ show, onHide, onComplete }) => {
  const { user } = useSelector(({ auth }) => ({ user: auth.user }));
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Definizione delle domande del questionario
  const questions = [
    {
      id: 'area_attivita',
      title: '1. Area di attività',
      subtitle: '',
      type: 'radio',
      options: ['Nord', 'Centro', 'Sud', 'Isole'],
      required: true
    },
    {
      id: 'anzianita_collaborazione',
      title: '2. Anzianità di collaborazione con l\'azienda',
      subtitle: '',
      type: 'radio',
      options: ['< 1 anno', '1–5 anni', '5-10 anni', '> 10 anni'],
      required: true
    },
    {
      id: 'tipologia_medici',
      title: '3. Tipologia di medici visitati abitualmente',
      subtitle: 'Indicare una percentuale approssimativa',
      type: 'percentage',
      fields: [
        'Medici di medicina generale',
        'Ginecologi',
        'Dermatologi',
        'Pediatri',
        'Chirurghi',
        'Dentisti',
        'Farmacie',
        'Altri'
      ],
      required: true
    },
    {
      id: 'frequenza_visite',
      title: '4. Con che frequenza visito lo stesso medico?',
      subtitle: '',
      type: 'radio',
      options: ['Settimanale', 'Quindicinale', 'Mensile', 'Occasionale', 'Solo su richiesta'],
      required: true
    },
    {
      id: 'prodotti_presentati',
      title: '5. Prodotti che presenti più spesso',
      subtitle: 'Fino a 3 prodotti',
      type: 'text',
      fields: ['1.', '2.', '3.'],
      required: true
    },
    {
      id: 'prodotto_facile',
      title: '6. Prodotto che ti risulta più facile da proporre e il motivo principale che ti spinge a proporlo di più',
      subtitle: '',
      type: 'textarea',
      required: true
    },
    {
      id: 'prodotto_difficile',
      title: '7. Prodotto che ti risulta più difficile da proporre e il motivo principale che crea difficoltà',
      subtitle: '', 
      type: 'textarea',
      required: true
    },
    {
      id: 'potenziale_crescita',
      title: '8. Dove vedi maggiore potenziale di crescita',
      subtitle: '',
      type: 'radio',
      options: ['Medicina generale', 'Ginecologia', 'Dermatologia', 'Pediatria', 'Chirurgia', 'Odontoiatria', 'Farmacia', 'Altro'],
      hasOther: true,
      required: true
    },
    {
      id: 'strumenti_utili',
      title: '9. Quali strumenti ti sarebbero più utili',
      subtitle: 'Massimo 2 opzioni',
      type: 'checkbox',
      options: [
        'Materiale Tipografico (ricettari, precompilati, cartelline, etc)',
        'Gadgets',
        'Campioni',
        'Studi clinici/dati scientifici',
        'Formazione scientifica',
        'Approfondimenti commerciali',
        'Promozioni/offerte',
        'Eventi/incontri',
        'Altro'
      ],
      maxSelections: 2,
      hasOther: true,
      required: true
    },
    {
      id: 'modalita_supporto',
      title: '10. In base alla tua esperienza, quale modalità di supporto informativo è generalmente più apprezzata dai medici del tuo territorio?',
      subtitle: '',
      type: 'radio',
      options: [
        'Materiale cartaceo (schede, brochure, studi stampati)',
        'Materiale digitale (PDF, link, QR code, email)',
        'Spiegazione verbale senza consegna di materiale',
        'Varia in base alla specializzazione'
      ],
      required: true
    },
    {
      id: 'valutazione_supporto',
      title: '11. Come valuti il supporto ricevuto dall\'azienda',
      subtitle: '',
      type: 'radio',
      options: ['Molto adeguato', 'Adeguato', 'Migliorabile', 'Insufficiente'],
      required: true
    },
    {
      id: 'preparazione_scientifica',
      title: '12. Come valuti la tua preparazione scientifica sui prodotti',
      subtitle: '(1 = poco, 5 = molto)',
      type: 'radio',
      options: ['1', '2', '3', '4', '5'],
      required: true
    },
    {
      id: 'migliorare_preparazione',
      title: '13. Come pensi possa migliorare la tua preparazione scientifica',
      subtitle: '',
      type: 'textarea',
      required: true
    },
    {
      id: 'aumentare_vendite',
      title: '14. Cosa ti aiuterebbe concretamente ad aumentare le vendite',
      subtitle: '',
      type: 'textarea',
      required: true
    },
    {
      id: 'segnalazioni_criticita',
      title: '15. Segnalazioni o criticità dal territorio',
      subtitle: '',
      type: 'textarea',
      required: true
    },
    {
      id: 'azione_concreta',
      title: '16. Quale azione concreta dovrebbe introdurre l\'azienda nel breve-medio termine?',
      subtitle: '',
      type: 'textarea',
      required: true
    }
  ];

  const totalSteps = questions.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleInputChange = (fieldId, value, description, subtitle) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value,
      [fieldId + '_descr']: description,
      
      [fieldId + '_subtitle']: subtitle
    }));
    setError('');
  };

  const validateCurrentStep = () => {
    const currentQuestion = questions[currentStep];
    
    if (currentQuestion.required) {
      if (currentQuestion.type === 'percentage') {
        const total = currentQuestion.fields.reduce((sum, field) => {
          const value = parseFloat(formData[`${currentQuestion.id}_${field}`] || 0);
          return sum + value;
        }, 0);
        
        if (total !== 100) {
          setError('La somma delle percentuali deve essere 100%');
          return false;
        }
      } else if (currentQuestion.type === 'checkbox') {
        const selected = currentQuestion.options.filter(opt => 
          formData[`${currentQuestion.id}_${opt}`]
        ).length;
        
        if (selected === 0) {
          setError('Seleziona almeno un\'opzione');
          return false;
        }
        
        if (currentQuestion.maxSelections && selected > currentQuestion.maxSelections) {
          setError(`Puoi selezionare massimo ${currentQuestion.maxSelections} opzioni`);
          return false;
        }
      } else if (currentQuestion.type === 'text' && currentQuestion.fields) {
        // Verifica se almeno un campo è compilato per le domande con campi multipli (es. prodotti presentati)
        const hasFilledField = currentQuestion.fields.some((_, index) => {
          const value = formData[`${currentQuestion.id}_${index}`];
          return value && value.trim() !== '';
        });
        
        if (!hasFilledField) {
          setError('Compila almeno un campo');
          return false;
        }
      } else if (!formData[currentQuestion.id] || (typeof formData[currentQuestion.id] === 'string' && formData[currentQuestion.id].trim() === '')) {
        setError('Questa domanda è obbligatoria');
        return false;
      }
    }
    
    return true;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep < totalSteps - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await post('questionnaire/SaveQuestionnaire', {
        jsonBody: {
          user_id: user.id,
          questionnaire_data: formData
        }
      });

      if (response && response.success) {
        onComplete();
        onHide();
      } else {
        setError(response?.message || 'Errore nel salvataggio del questionario');
      }
    } catch (error) {
      setError('Errore di connessione. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = () => {
    const question = questions[currentStep];
    
    switch (question.type) {
      case 'radio':
        return (
          <Form.Group>
            <Form.Label className="font-weight-bold mb-3"><h4>{question.title}</h4><div className='text-black-50 mb-2'>{question.subtitle}</div></Form.Label>
            <div className="d-flex flex-column">
              {question.options.map((option, index) => {
                const isSelected = formData[question.id] === option;
                const variantType = getDatabaseNamePath() === 'valderma' ? 'primary' : 'danger';
                
                return (
                  <Button
                    key={index}
                    variant={isSelected ? variantType : `outline-${variantType}`}
                    className={`text-left mb-2 d-flex align-items-center justify-content-between p-3 ${isSelected ? 'font-weight-bold shadow-sm' : ''}`}
                    onClick={() => handleInputChange(question.id, option, question.title)}
                    style={{ 
                      transition: 'all 0.2s',
                      borderWidth: '2px',
                      fontSize: '1.1rem'
                    }}
                  >
                    <span>{option}</span>
                    {isSelected && <i className="fas fa-check-circle"></i>}
                  </Button>
                );
              })}
            </div>
            {question.hasOther && formData[question.id] === 'Altro' && (
              <Form.Control
                type="text"
                placeholder="Specifica..."
                value={formData[`${question.id}_other`] || ''}
                onChange={(e) => handleInputChange(`${question.id}_other`, e.target.value, question.title, question.subtitle)}
                className="mt-2"
              />
            )}
          </Form.Group>
        );

      case 'checkbox':
        return (
          <Form.Group>
            <Form.Label className="font-weight-bold mb-3"><h4>{question.title}</h4><div className='text-black-50 mb-2'>{question.subtitle}</div></Form.Label>
            <div className="d-flex flex-column">
              {question.options.map((option, index) => {
                const isChecked = formData[`${question.id}_${option}`] || false;
                const variantType = getDatabaseNamePath() === 'valderma' ? 'primary' : 'danger';
                
                // Calcola quante opzioni sono già selezionate
                const selectedCount = question.options.filter(opt => formData[`${question.id}_${opt}`]).length;
                // Disabilita se abbiamo raggiunto il limite e questa opzione non è selezionata
                const isDisabled = !isChecked && question.maxSelections && selectedCount >= question.maxSelections;

                return (
                  <Button
                    key={index}
                    variant={isChecked ? variantType : `outline-${variantType}`}
                    className={`text-left mb-2 d-flex align-items-center justify-content-between p-3 ${isChecked ? 'font-weight-bold shadow-sm' : ''}`}
                    onClick={() => {
                      if (!isDisabled) {
                        handleInputChange(`${question.id}_${option}`, !isChecked, question.title);
                      }
                    }}
                    disabled={isDisabled}
                    style={{ 
                      transition: 'all 0.2s',
                      borderWidth: '2px',
                      fontSize: '1.1rem',
                      opacity: isDisabled ? 0.6 : 1
                    }}
                  >
                    <span>{option}</span>
                    {isChecked && <i className="fas fa-check-square"></i>}
                  </Button>
                );
              })}
            </div>
            {question.hasOther && formData[`${question.id}_Altro`] && (
              <Form.Control
                type="text"
                placeholder="Specifica..."
                value={formData[`${question.id}_other`] || ''}
                onChange={(e) => handleInputChange(`${question.id}_other`, e.target.value, question.title)}
                className="mt-2"
              />
            )}
          </Form.Group>
        );

      case 'text':
        return (
          <Form.Group>
            <Form.Label className="font-weight-bold"><h4>{question.title}</h4><div className='text-black-50 mb-2'>{question.subtitle}</div></Form.Label>
            {question.fields.map((field, index) => (
              <Form.Control
                key={index}
                type="text"
                placeholder={field}
                value={formData[`${question.id}_${index}`] || ''}
                onChange={(e) => handleInputChange(`${question.id}_${index}`, e.target.value, question.title)}
                className="mb-2"
              />
            ))}
          </Form.Group>
        );

      case 'textarea':
        return (
          <Form.Group>
            <Form.Label className="font-weight-bold"><h4>{question.title}</h4><div className='text-black-50 mb-2'>{question.subtitle}</div></Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={formData[question.id] || ''}
              onChange={(e) => handleInputChange(question.id, e.target.value, question.title)}
              placeholder="Inserisci la tua risposta..."
            />
          </Form.Group>
        );

      case 'percentage':
        return (
          <Form.Group>
            <Form.Label className="font-weight-bold">
              <h4>{question.title}</h4>
              <div className='text-black-50 mb-2'>{question.subtitle}</div>
            </Form.Label>
            <div className="row">
              {question.fields.map((field, index) => (
                <div key={index} className="col-md-6 mb-3">
                  <Form.Label className="small font-weight-bold">{field}</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      value={formData[`${question.id}_${field}`] || ''}
                      onChange={(e) => handleInputChange(`${question.id}_${field}`, e.target.value, question.title)}
                      placeholder="0"
                      className="form-control-lg"
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Form.Text className="text-muted mt-2">
              <i className="fas fa-calculator mr-1"></i>
              La somma deve essere 100%. Totale attuale: {
                question.fields.reduce((sum, field) => sum + parseFloat(formData[`${question.id}_${field}`] || 0), 0)
              }%
            </Form.Text>
          </Form.Group>
        );

      default:
        return null;
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      backdrop="static" 
      keyboard={false}
      centered
    >
      <Modal.Header style={{ backgroundImage: `url(${toAbsoluteUrl("/media/bg/bg.jpg")})`, backgroundSize: 'cover',}}>
        <Modal.Title className="text-white">
          {/* <i className="fas fa-clipboard-list mr-2"></i> */}
          <h5 className="mb-0">Questionario Rete Vendita - Attività sul Territorio</h5>
          <small className={`d-block mt-2 ${getDatabaseNamePath() == 'valderma' ? '' : 'text-white' }`}>
          Il questionario ha l’ obiettivo di comprendere meglio l’attività sul territorio per orientare in modo più mirato materiali, priorità e supporto alla rete.</small>
        </Modal.Title>
        
      </Modal.Header>
      
      <Modal.Body className="p-4">
        <div className="mb-4">
          <ProgressBar 
            now={progress} 
            label={`${Math.round(progress)}%`}
            variant={getDatabaseNamePath() == 'valderma' ? 'primary' : 'danger'}
            animated
          />
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Form>
          {renderQuestion()}
        </Form>

        <div className="text-muted small mt-3">
          <i className="fas fa-info-circle mr-1"></i>
          Tutte le domande sono obbligatorie. Tempo stimato: 3 minuti.
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={handlePrevious}
          disabled={currentStep === 0}
        >
          <i className="fas fa-arrow-left mr-1"></i>
          Precedente
        </Button>
        
        {currentStep < totalSteps - 1 ? (
          <Button variant={getDatabaseNamePath() == 'valderma' ? 'primary' : 'danger'} onClick={handleNext}>
            Avanti
            <i className="fas fa-arrow-right ml-1"></i>
          </Button>
        ) : (
          <Button 
            variant="success" 
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin mr-1"></i>
                Invio in corso...
              </>
            ) : (
              <>
                Invia Questionario
                <i className="fas fa-paper-plane ml-1"></i>
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default QuestionnaireModal;